import { useState, useEffect, useRef } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  MessageSquare, Search, ArrowLeft, AlertTriangle, User, Loader2, Shield,
} from 'lucide-react';
import { Watermark } from '../components/Watermark';
import { ConfidentialBanner } from '../components/ConfidentialBanner';
import { useContentProtection } from '../hooks/useContentProtection';

export default function AdminMessagesPage() {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);

  const searchUsers = async (e) => {
    e?.preventDefault();
    if (!search.trim()) return;
    setLoading(true);
    try {
      const data = await api.adminGetUsers({ search, limit: 10 });
      setUsers(data.users || []);
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  const selectUser = async (user) => {
    setSelectedUser(user);
    setConversations([]);
    setMessages([]);
    setSelectedPartner(null);
    setLoading(true);
    try {
      const data = await api.adminUserConversations(user.id);
      setConversations(data.conversations || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const openThread = async (partnerId) => {
    setSelectedPartner(partnerId);
    setMsgLoading(true);
    try {
      const data = await api.adminGetMessages(selectedUser.id, partnerId);
      setMessages(data.messages || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMsgLoading(false); }
  };

  const protectedRef = useRef(null);
  useContentProtection(protectedRef);

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-messages-page" ref={protectedRef}>
      <div className="flex items-center gap-3 mb-4">
        {(selectedUser || selectedPartner) && (
          <button onClick={() => {
            if (selectedPartner) { setSelectedPartner(null); setMessages([]); }
            else { setSelectedUser(null); setConversations([]); }
          }} className="p-2 rounded-lg hover:bg-white/5 text-zinc-400">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">
            {selectedPartner ? 'Conversacion' : selectedUser ? `Mensajes de ${selectedUser.name}` : 'Vigilancia de Mensajes'}
          </h1>
          <p className="text-xs text-zinc-600">
            {selectedPartner ? `${selectedUser?.name} ↔ ${conversations.find(c => c.partner_id === selectedPartner)?.partner_name || ''}` : 'Busca un usuario para ver sus conversaciones'}
          </p>
        </div>
      </div>
      <ConfidentialBanner />

      {/* Step 1: Search users */}
      {!selectedUser && (
        <div>
          <form onSubmit={searchUsers} className="flex gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
              <Input placeholder="Buscar usuario por nombre o email..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-10 bg-white/[0.03] border-white/5 text-zinc-100 h-9 text-sm" data-testid="msg-user-search" />
            </div>
            <Button type="submit" variant="outline" className="border-white/5 text-zinc-300 text-xs h-9" disabled={loading}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Buscar'}
            </Button>
          </form>

          <div className="space-y-2">
            {users.map(u => (
              <button key={u.id} onClick={() => selectUser(u)}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-white/[0.04] bg-[#0A0A0D] hover:bg-white/[0.02] transition-colors text-left"
                data-testid={`msg-user-${u.id}`}>
                <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 text-sm font-medium">
                  {(u.name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-200 font-medium truncate">{u.name}</p>
                  <p className="text-[10px] text-zinc-600">{u.email} - {u.role}</p>
                </div>
                <MessageSquare className="w-4 h-4 text-zinc-600" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Conversations list */}
      {selectedUser && !selectedPartner && (
        <div>
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-12 text-zinc-600 text-sm">Este usuario no tiene conversaciones</div>
          ) : (
            <div className="space-y-2">
              {conversations.map(conv => (
                <button key={conv.partner_id} onClick={() => openThread(conv.partner_id)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-white/[0.04] bg-[#0A0A0D] hover:bg-white/[0.02] transition-colors text-left"
                  data-testid={`conv-${conv.partner_id}`}>
                  <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 text-sm font-medium">
                    {(conv.partner_name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-zinc-200 font-medium">{conv.partner_name}</p>
                      <span className="text-[10px] text-zinc-600">{conv.partner_email}</span>
                    </div>
                    <p className="text-xs text-zinc-500 truncate mt-0.5">{conv.last_message}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-[10px] text-zinc-600">{conv.count} msgs</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Message thread */}
      {selectedPartner && (
        <Watermark opacity={0.05}>
        <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-4" data-testid="message-thread" style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
          {msgLoading ? (
            <div className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-zinc-600 text-sm">Sin mensajes</div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {messages.map((msg, i) => {
                const isTarget = msg.sender_id === selectedUser.id;
                const hasFlags = msg.risk_flags?.length > 0;
                return (
                  <div key={msg.id || `msg-${i}`} className={`flex ${isTarget ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[70%] rounded-xl p-3 ${
                      hasFlags ? 'bg-red-500/10 border border-red-500/20' :
                      isTarget ? 'bg-white/[0.04]' : 'bg-blue-500/10'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] text-zinc-500 font-medium">
                          {isTarget ? selectedUser.name : conversations.find(c => c.partner_id === selectedPartner)?.partner_name}
                        </span>
                        <span className="text-[10px] text-zinc-700">
                          {msg.created_at ? new Date(msg.created_at).toLocaleString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-200">{msg.content}</p>
                      {hasFlags && (
                        <div className="flex items-center gap-1 mt-2 text-red-400 text-[10px]">
                          <AlertTriangle className="w-3 h-3" />
                          <span>Alerta: {msg.risk_flags.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </Watermark>
      )}
    </div>
  );
}
